// react
import { useCallback } from 'react';

// kinde
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

// store
import store, { dispatch } from 'store';
import {
  clearAuth,
  getIsSigningOut,
  setIsSigningOut,
} from 'store/slices/iamSlice';
import { setChatToken } from 'store/slices/chatSlice';
import { clearCareCircle } from 'store/slices/careCircleSlice';
import { Capacitor } from '@capacitor/core';
import { Intercom as CapacitorIntercom } from '@capacitor-community/intercom';

// sentry
import * as Sentry from '@sentry/react';

// hooks
import { useTracking } from './useTracking';

import { exchangeAuthCode } from '@kinde-oss/kinde-auth-react/utils';

import { ROUTES } from 'router';

const NATIVE_AUTH_SCHEME = 'karehero://';
const NATIVE_AUTH_REDIRECT_URI = `${NATIVE_AUTH_SCHEME}authenticate/native`;
const KINDE_TOKEN_DOMAIN = import.meta.env.REACT_APP_KINDE_TOKEN_DOMAIN;
const KINDE_TOKEN_ID = import.meta.env.REACT_APP_KINDE_TOKEN_CLIENT_ID;

const env = import.meta.env.REACT_APP_BUILD_ENVIRONMENT;
const ConnectionID = {
  GOOGLE:
    env === 'production'
      ? 'conn_018f08586998f0eac9cfe1e83083a85d'
      : 'conn_0198096382d50b66b61ce5d51fae0a61',
  FACEBOOK: 'conn_018f085869984eededa5e9c89ea51977',
  APPLE: 'conn_018f0858699813cd2bcae3e27246eddf',
  MICROSOFT: 'conn_01997f1452134f1d15219b7d050a8461',
  EMAIL:
    env === 'production'
      ? 'conn_018f085869975f5b856eda3f9e4e3df6'
      : 'conn_018f085869531133e802e4ce49939ea9',
};

export const useAuth = () => {
  // hooks
  const {
    login: loginKinde,
    register: registerKinde,
    logout: logoutKinde,
    isAuthenticated,
    isLoading,
    getToken,
  } = useKindeAuth();
  const { reset: resetTracking } = useTracking();

  // methods
  const signIn = useCallback(
    (connectionID?: string, email?: string) => {
      const options: any = {
        state: {},
      };

      if (connectionID) options.connectionId = connectionID;
      if (email) options.loginHint = email;

      loginKinde(options);
    },
    [loginKinde],
  );

  const register = useCallback(
    (connectionID?: string, email?: string, registerToken?: string) => {
      const options: any = {
        prompt: 'create',
        state: registerToken ? { token: registerToken } : {},
      };

      if (connectionID) options.connectionId = connectionID;
      if (email) options.loginHint = email;

      registerKinde(options);
    },
    [registerKinde],
  );

  const clearChatState = useCallback(() => {
    dispatch(setChatToken(null));
  }, []);

  const logoutIntercom = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      CapacitorIntercom.logout();
    } else {
      window.Intercom?.('shutdown');
    }
  }, []);

  // methods
  const clearAuthState = useCallback(() => {
    // localStorage
    const state = localStorage.getItem('reduxState');
    const reduxState = JSON.parse(state || '{}');
    delete reduxState.iam;
    delete reduxState.chat;
    delete reduxState.careCircle;
    localStorage.setItem('reduxState', JSON.stringify(reduxState));

    // redux
    dispatch(clearAuth());
    dispatch(clearCareCircle());
  }, []);

  const signOut = useCallback(async () => {
    // A second sign-out would open a second Kinde logout popover
    if (getIsSigningOut(store.getState())) return;
    // Stops the app signing the account back in while signing out
    dispatch(setIsSigningOut(true));

    // Request Kinde's signed-out state before clearing the account. This orders
    // the two updates, not their renders, so isSigningOut is what guarantees
    // the app doesn't sign the account back in between them
    try {
      await logoutKinde();
    } catch (err) {
      console.error('[useAuth] Kinde logout failed:', err);
      Sentry.captureException(err, { tags: { 'auth.action': 'logout' } });
      // Kinde may still be signed in, so keep the account rather than leave
      // the app waiting on a sign-in that isSigningOut would hold back
      dispatch(setIsSigningOut(false));
      return;
    }
    clearChatState();
    resetTracking();
    logoutIntercom();
    clearAuthState();
  }, [
    clearAuthState,
    clearChatState,
    resetTracking,
    logoutKinde,
    logoutIntercom,
  ]);

  /**
   * Handles the native OAuth callback URL (e.g. karehero://authenticate/native?code=...).
   * Exchanges the code for tokens so Kinde storage is populated; the caller is responsible
   * for closing IAB and navigating (e.g. to '/').
   * Returns { handled: true } only when the URL was an auth callback and exchange succeeded.
   */
  const handleNativeAuthCallback = useCallback(async (uri: string) => {
    if (!Capacitor.isNativePlatform()) return { handled: false };
    if (!uri.startsWith(NATIVE_AUTH_SCHEME)) return { handled: false };
    if (!KINDE_TOKEN_DOMAIN || !KINDE_TOKEN_ID) {
      console.error('[useAuth] domain or clientId not found');
      return { handled: false };
    }

    const normalized = uri.replace(
      NATIVE_AUTH_SCHEME,
      `${window.location.origin}/`,
    );

    let url: URL;
    try {
      url = new URL(normalized);
    } catch {
      return { handled: false };
    }

    const isAuthPath =
      url.pathname === ROUTES.AUTHENTICATE ||
      url.pathname === ROUTES.AUTHENTICATE_NATIVE;
    if (!isAuthPath || !url.searchParams.get('code')) return { handled: false };

    try {
      const result = await exchangeAuthCode({
        urlParams: url.searchParams,
        domain: KINDE_TOKEN_DOMAIN,
        clientId: KINDE_TOKEN_ID,
        redirectURL: NATIVE_AUTH_REDIRECT_URI,
        autoRefresh: true,
        onRefresh: () => {},
      });

      return { handled: result.success };
    } catch (e) {
      console.error('[useAuth] Native auth code exchange failed:', e);
      return { handled: false };
    }
  }, []);

  return {
    signIn,
    signOut,
    register,
    clearAuthState,
    getToken,
    isAuthenticated,
    isLoading,
    ConnectionID,
    handleNativeAuthCallback,
  };
};
